import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Building2, Plus, Trash2, UserPlus, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLANS = ["pro", "intelligence", "enterprise"];

export const OrgManager = () => {
  const [orgs, setOrgs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [members, setMembers] = useState({});
  const [form, setForm] = useState({ name: "", plan: "intelligence", seats: 5 });
  const [memberEmail, setMemberEmail] = useState("");

  const toggleOrgAddon = async (org, key, on) => {
    const addons = on ? (org.addons || []).filter((a) => a !== key) : [...(org.addons || []), key];
    try {
      await axios.patch(`${API}/orgs/${org.org_id}`, { addons });
      toast.success(`${org.name}: ${key} add-on ${on ? "removed" : "enabled"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update add-ons");
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/orgs`);
      setOrgs(r.data || []);
    } catch {
      setOrgs([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadMembers = async (orgId) => {
    try {
      const r = await axios.get(`${API}/orgs/${orgId}/members`);
      setMembers((m) => ({ ...m, [orgId]: r.data || [] }));
    } catch {
      setMembers((m) => ({ ...m, [orgId]: [] }));
    }
  };

  const createOrg = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/orgs`, { name: form.name.trim(), plan: form.plan, seat_limit: Number(form.seats) || 5 });
      toast.success(`Created ${form.name.trim()}`);
      setForm({ name: "", plan: "intelligence", seats: 5 });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed");
    }
  };

  const deleteOrg = async (org) => {
    if (!window.confirm(`Delete ${org.name}? Members keep their accounts but lose team access.`)) return;
    try {
      await axios.delete(`${API}/orgs/${org.org_id}`);
      toast.success(`Deleted ${org.name}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const addMember = async (org) => {
    if (!memberEmail.trim()) return;
    try {
      await axios.post(`${API}/orgs/${org.org_id}/members`, { email: memberEmail.trim(), org_role: "member" });
      toast.success(`Added ${memberEmail.trim()}`);
      setMemberEmail("");
      loadMembers(org.org_id);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Add failed");
    }
  };

  const removeMember = async (org, m) => {
    try {
      await axios.delete(`${API}/orgs/${org.org_id}/members/${m.user_id}`);
      toast.success(`Removed ${m.email}`);
      loadMembers(org.org_id);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    }
  };

  const toggleRole = async (org, m) => {
    const role = m.organization_role === "org_admin" ? "member" : "org_admin";
    try {
      await axios.patch(`${API}/orgs/${org.org_id}/members/${m.user_id}`, { org_role: role });
      toast.success(`${m.email} → ${role}`);
      loadMembers(org.org_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div className="mt-10" data-testid="org-manager">
      <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
        <Building2 className="w-4 h-4 text-orange-400" /> Organizations
      </h2>
      <p className="text-xs text-white/45 mb-4">
        Team accounts for WHIP Intelligence, members inherit the organization's plan.
      </p>

      <form onSubmit={createOrg} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-4 flex flex-wrap gap-2 items-center">
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Organization name"
          required
          className="flex-1 min-w-[180px] rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-orange-500/60"
          data-testid="org-name-input"
        />
        <select
          value={form.plan}
          onChange={(e) => setForm({ ...form, plan: e.target.value })}
          className="rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-orange-500/60"
          data-testid="org-plan-select"
        >
          {PLANS.map((p) => (
            <option key={p} value={p} className="bg-[#12121a]">{p.charAt(0).toUpperCase() + p.slice(1)} plan</option>
          ))}
        </select>
        <input
          type="number"
          min="1"
          max="1000"
          value={form.seats}
          onChange={(e) => setForm({ ...form, seats: e.target.value })}
          className="w-20 rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-orange-500/60"
          title="Seat limit"
          data-testid="org-seats-input"
        />
        <button
          type="submit"
          className="inline-flex items-center gap-1.5 rounded-lg bg-orange-600 hover:bg-orange-500 px-4 py-2 text-sm font-bold text-white transition-colors"
          data-testid="org-create-button"
        >
          <Plus className="w-4 h-4" /> Create
        </button>
      </form>

      <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
        {orgs.length === 0 && (
          <p className="px-5 py-4 text-sm text-white/40" data-testid="orgs-empty">No organizations yet.</p>
        )}
        {orgs.map((org, i) => (
          <div key={org.org_id}>
            <div className="px-5 py-3.5">
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => {
                    const next = expanded === org.org_id ? null : org.org_id;
                    setExpanded(next);
                    if (next) loadMembers(org.org_id);
                  }}
                  className="flex items-center gap-2 text-white font-semibold text-sm hover:text-orange-300 transition-colors"
                  data-testid={`org-expand-${org.name}`}
                >
                  {expanded === org.org_id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {org.name}
                </button>
                <span className="whip-pill !py-0.5 !px-2 !text-[9px] !text-orange-300 !border-orange-500/40 !bg-orange-500/10">
                  {org.plan.toUpperCase()}
                </span>
                <span className="text-[11px] font-mono text-white/40">
                  {org.member_count}/{org.seat_limit} seats · {org.status.toUpperCase()}
                </span>
                <div className="flex items-center gap-1" data-testid={`org-addons-${org.name}`}>
                  {[["tools", "Tools"], ["network", "Network"]].map(([key, label]) => {
                    const on = (org.addons || []).includes(key);
                    return (
                      <button key={key} type="button" title={`${label} add-on for every member`}
                        onClick={() => toggleOrgAddon(org, key, on)}
                        className={`rounded-md border px-2 py-0.5 text-[10px] font-mono uppercase transition-colors ${on ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-white/15 bg-white/[0.03] text-white/40 hover:text-white/70"}`}
                        data-testid={`org-addon-${key}-${org.name}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex-1" />
                <button
                  onClick={() => deleteOrg(org)}
                  className="text-white/30 hover:text-red-400 transition-colors"
                  title="Delete organization"
                  data-testid={`org-delete-${org.name}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {expanded === org.org_id && (
                <div className="mt-3 ml-1 pl-4 border-l border-white/10">
                  <div className="flex gap-2 mb-2.5">
                    <input
                      value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      placeholder="Add member by account email"
                      className="flex-1 rounded-lg border border-white/15 bg-white/[0.03] px-3 py-1.5 text-xs text-white placeholder:text-white/30 outline-none focus:border-orange-500/60"
                      data-testid={`org-member-email-${org.name}`}
                    />
                    <button
                      onClick={() => addMember(org)}
                      className="inline-flex items-center gap-1 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 text-xs font-bold text-orange-300 hover:bg-orange-500/20 transition-colors"
                      data-testid={`org-member-add-${org.name}`}
                    >
                      <UserPlus className="w-3.5 h-3.5" /> Add
                    </button>
                  </div>
                  {(members[org.org_id] || []).map((m) => (
                    <div key={m.user_id} className="flex items-center gap-2.5 py-1.5 text-xs">
                      <span className="text-white/75 font-mono flex-1 truncate">{m.email}</span>
                      <button
                        onClick={() => toggleRole(org, m)}
                        className={`rounded-full border px-2 py-0.5 text-[9px] font-mono font-bold uppercase tracking-wider transition-colors ${
                          m.organization_role === "org_admin"
                            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                            : "border-white/15 text-white/40 hover:text-emerald-300"
                        }`}
                        title="Toggle org admin"
                        data-testid={`org-member-role-${m.email}`}
                      >
                        {m.organization_role === "org_admin" ? "ORG ADMIN" : "MEMBER"}
                      </button>
                      <button
                        onClick={() => removeMember(org, m)}
                        className="text-white/30 hover:text-red-400 transition-colors"
                        data-testid={`org-member-remove-${m.email}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {(members[org.org_id] || []).length === 0 && (
                    <p className="text-[11px] text-white/35 py-1">No members yet.</p>
                  )}
                </div>
              )}
            </div>
            {i < orgs.length - 1 && <div className="whip-divider" />}
          </div>
        ))}
      </div>
    </div>
  );
};
